/**
 * PostHog Analytics Integration for Quilty
 * 
 * Provides behavioral analytics tracking via PostHog:
 * - Automatic page view tracking
 * - User identification
 * - Event tracking
 * - Session recording (optional)
 */

import { browser } from '$app/environment';
import { getPublicEnvRecord } from '$lib/env/publicRuntime';
import {
  isContentSurface,
  pathnameOnly,
  sanitizeAnalyticsProperties,
} from '$lib/analytics/content-telemetry';
import {
  buildPostHogSurveyPersonProperties,
  type PostHogSurveyPersonSource,
} from '$lib/analytics/posthog-person-properties';

let posthogInstance: any = null;
let isInitialized = false;
// Tracks whether the visitor has granted full (cookie-based) consent. When
// false, PostHog runs in a privacy-preserving "cookieless" mode: memory-only
// persistence (no cross-session identity), no session recording, and no
// user identification. Flipped by initPostHog(consented) / setPostHogConsent.
let hasFullConsent = false;

/** Referrer patterns for AI answer engines (GEO citation traffic). */
const AI_REFERRER_PATTERNS: readonly RegExp[] = [
  /chat\.openai\.com/i,
  /chatgpt\.com/i,
  /perplexity\.ai/i,
  /claude\.ai/i,
  /anthropic\.com/i,
  /copilot\.microsoft\.com/i,
  /gemini\.google\.com/i,
  /you\.com/i,
  /phind\.com/i,
  /poe\.com/i,
  /searchgpt\.com/i,
];

export type ReferrerSegment =
  | 'ai_citation'
  | 'organic_google'
  | 'social'
  | 'direct'
  | 'other';

/** Classify document.referrer for PostHog segmentation (especially AI/GEO). */
export function classifyReferrer(referrer: string): ReferrerSegment {
  if (!referrer) return 'direct';
  for (const pattern of AI_REFERRER_PATTERNS) {
    if (pattern.test(referrer)) return 'ai_citation';
  }
  if (/google\./i.test(referrer)) return 'organic_google';
  if (/facebook\.|twitter\.|x\.com|linkedin\.|instagram\.|t\.co/i.test(referrer)) {
    return 'social';
  }
  return 'other';
}

function applyReferrerSegment(ph: any): void {
  if (!browser || !document.referrer) return;
  const segment = classifyReferrer(document.referrer);
  ph.register({ referrer_segment: segment });
  if (segment === 'ai_citation') {
    ph.capture('ai_referrer_visit', {
      referrer: document.referrer,
      landing_path: window.location.pathname,
    });
  }
}

/**
 * Initialise PostHog.
 *
 * @param consented When true (explicit accept, or opt-out region by default),
 *   PostHog persists identity in localStorage and records sessions. When
 *   false, it runs cookieless: `persistence: 'memory'` (no cookies/localStorage,
 *   no cross-session identity) and session recording disabled. This lets us
 *   measure aggregate pageviews/funnels for every visitor without dropping a
 *   cookie before consent — the pattern PostHog documents for GDPR.
 */
export async function initPostHog(consented: boolean = true): Promise<boolean> {
  if (!browser) return false;
  hasFullConsent = consented;
  if (isInitialized) {
    // Already running (e.g. cookieless): just move it to the new consent level.
    setPostHogConsent(consented);
    return true;
  }

  const publicEnv = getPublicEnvRecord();

  const POSTHOG_KEY =
    publicEnv.PUBLIC_POSTHOG_KEY ||
    (import.meta.env?.VITE_POSTHOG_KEY as string | undefined) ||
    '';

  const CLOUD_HOST =
    publicEnv.PUBLIC_POSTHOG_HOST ||
    (import.meta.env?.VITE_POSTHOG_HOST as string | undefined) ||
    'https://us.i.posthog.com';

  // Prefer the first-party nginx reverse proxy (/ingest -> PostHog cloud) on
  // our own domains to dodge ad blockers that drop requests to *.posthog.com.
  // The proxy is configured in the live nginx for quilty.app and *.quilty.app
  // (see nginx-quilty-app.conf / nginx-dwood-dev.conf). Any other host (e.g.
  // localhost in a cloud agent) falls back to the direct cloud endpoint.
  let POSTHOG_HOST = CLOUD_HOST;
  if (browser) {
    const host = window.location.hostname;
    if (host === 'quilty.app' || host.endsWith('.quilty.app')) {
      POSTHOG_HOST = `${window.location.origin}/ingest`;
    }
  }

  // Skip if no API key configured
  if (!POSTHOG_KEY) {
    console.debug('[PostHog] No API key configured, analytics disabled');
    return false;
  }
  
  try {
    const posthog = await import('posthog-js');
    
    posthog.default.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // When using a reverse proxy, ui_host tells PostHog where to load the
      // toolbar/UI from (must be the real cloud host, not the proxy).
      ui_host: 'https://us.i.posthog.com',
      autocapture: !isContentSurface(window.location.pathname),
      capture_pageview: true,
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
      },
      // Cookieless until consent: memory persistence drops no cookie/localStorage
      // and gives no cross-session identity. Upgraded to localStorage on accept.
      persistence: consented ? 'localStorage' : 'memory',
      // Session replay stays off. Consent does not enable it: authenticated
      // content surfaces can show screenplay and report text.
      disable_session_recording: true,
      loaded: (ph) => {
        posthogInstance = ph;
        isInitialized = true;
        applyReferrerSegment(ph);
        console.debug(
          `[PostHog] Initialized (${consented ? 'consented' : 'cookieless'})`,
        );
      },
    });
    
    posthogInstance = posthog.default;
    isInitialized = true;
    applyReferrerSegment(posthog.default);
    return true;
  } catch (error) {
    console.error('[PostHog] Failed to initialize:', error);
    return false;
  }
}

/**
 * Check if PostHog is ready
 */
export function isPostHogReady(): boolean {
  return isInitialized && posthogInstance !== null;
}

/**
 * Transition an already-running PostHog instance between cookieless and
 * fully-consented modes without a reload. On upgrade to consent we switch to
 * localStorage persistence and (re)enable session recording; on downgrade we
 * revert to memory-only persistence and stop recording.
 */
export function setPostHogConsent(consented: boolean): void {
  hasFullConsent = consented;
  if (!isPostHogReady()) return;
  try {
    posthogInstance.set_config({
      persistence: consented ? 'localStorage' : 'memory',
      disable_session_recording: true,
    });
    posthogInstance.stopSessionRecording?.();
    if (!consented) {
      posthogInstance.reset?.();
    }
  } catch (error) {
    console.error('[PostHog] Failed to update consent config:', error);
  }
}

/**
 * Identify a user in PostHog
 * Call after login/signup
 */
export function identifyUser(
  userId: number | string,
  properties?: PostHogSurveyPersonSource & {
    email?: string;
    displayName?: string;
    isAdmin?: boolean;
    betaEnrolled?: boolean;
    createdAt?: string;
    [key: string]: any;
  }
): void {
  if (!isPostHogReady()) return;
  // Identity is cross-session PII: only bind it for consented visitors.
  // Cookieless (pre-consent) sessions stay anonymous.
  if (!hasFullConsent) return;

  try {
    const sanitized = { ...properties };
    if (sanitized?.email) {
      delete sanitized.email;
    }

    const surveyPerson = buildPostHogSurveyPersonProperties(sanitized ?? {});
    const { displayName, isAdmin, betaEnrolled } = sanitized;

    // posthog-js identify(id, $set, $set_once). Nesting `$set_once` inside the
    // second argument never wrote signup_date onto the person (0 coverage) and
    // broke Survey 4 tenure gating (QUI-281).
    posthogInstance.identify(
      String(userId),
      {
        ...(displayName != null ? { displayName } : {}),
        ...(isAdmin != null ? { isAdmin } : {}),
        ...(betaEnrolled != null ? { betaEnrolled } : {}),
        ...(surveyPerson ?? {}),
      },
      {
        first_seen: new Date().toISOString(),
        ...(surveyPerson?.signup_date ? { signup_date: surveyPerson.signup_date } : {}),
      },
    );
    console.debug('[PostHog] User identified:', userId);
  } catch (error) {
    console.error('[PostHog] Failed to identify user:', error);
  }
}

/**
 * Reset user identity (call on logout)
 */
export function resetUser(): void {
  if (!isPostHogReady()) return;
  
  try {
    posthogInstance.reset();
    console.debug('[PostHog] User reset');
  } catch (error) {
    console.error('[PostHog] Failed to reset user:', error);
  }
}

/**
 * Track a custom event
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, any>
): void {
  if (!isPostHogReady()) {
    // Fallback to console logging in development
    console.debug(`[Analytics] ${eventName}`, properties);
    return;
  }
  
  try {
    const pathname = browser ? window.location.pathname : '';
    posthogInstance.capture(
      eventName,
      sanitizeAnalyticsProperties(properties, pathname),
    );
  } catch (error) {
    console.error('[PostHog] Failed to track event:', error);
  }
}

/**
 * Track page view (automatic with autocapture, but can be called manually)
 */
export function trackPageView(
  pagePath?: string,
  properties?: Record<string, any>
): void {
  if (!isPostHogReady()) return;
  
  try {
    const href = browser ? window.location.href : '';
    posthogInstance.capture('$pageview', {
      $current_url: pathnameOnly(pagePath || href),
      referrer_segment: browser ? classifyReferrer(document.referrer) : undefined,
      ...sanitizeAnalyticsProperties(properties, pathnameOnly(pagePath || href)),
    });
  } catch (error) {
    console.error('[PostHog] Failed to track page view:', error);
  }
}

/**
 * Track feature usage
 */
export function trackFeature(
  featureName: string,
  properties?: Record<string, any>
): void {
  trackEvent(`feature:${featureName}`, properties);
}

/**
 * Track screenplay-related events
 */
export const screenplayEvents = {
  uploaded: (screenplayId: number, format: string, sizeKb: number) =>
    trackEvent('screenplay_uploaded', { screenplayId, format, sizeKb }),
  
  previewViewed: (screenplayId: number) =>
    trackEvent('screenplay_preview_viewed', { screenplayId }),
  
  analysisStarted: (screenplayId: number, budgetTier?: string) =>
    trackEvent('analysis_started', { screenplayId, budgetTier }),
  
  analysisCompleted: (screenplayId: number, analysisId: number, durationMs: number) =>
    trackEvent('analysis_completed', { screenplayId, analysisId, durationMs }),
  
  analysisFailed: (screenplayId: number, error: string) =>
    trackEvent('analysis_failed', { screenplayId, error: error.substring(0, 200) }),
  
  deleted: (screenplayId: number) =>
    trackEvent('screenplay_deleted', { screenplayId }),
};

/**
 * Track TheBiz feature usage
 */
export const theBizEvents = {
  searched: (query: string, resultsCount: number) =>
    trackEvent('thebiz_search', { query: query.substring(0, 100), resultsCount }),
  
  companyViewed: (companyId: string) =>
    trackEvent('thebiz_company_viewed', { companyId }),
  
  exportClicked: (format: string) =>
    trackEvent('thebiz_export_clicked', { format }),
};

/**
 * Track Studio/Production Hub usage
 */
export const studioEvents = {
  moduleOpened: (moduleName: string, screenplayId?: number) =>
    trackEvent('studio_module_opened', { moduleName, screenplayId }),

  reportGenerated: (reportType: string, screenplayId: number) =>
    trackEvent('studio_report_generated', { reportType, screenplayId }),

  taskCreated: (taskType: string, screenplayId: number) =>
    trackEvent('studio_task_created', { taskType, screenplayId }),
};

/**
 * Track Radio Play (adaptive scoring) usage
 */
export const radioPlayEvents = {
  modeSelected: (screenplayId: number, provider?: string | null) =>
    trackEvent('radio_play_mode_selected', { screenplayId, provider }),

  productionConfigured: (
    screenplayId: number,
    options: {
      renderMode?: string;
      action?: string;
      includeAmbience?: boolean;
      includeFoley?: boolean;
      includeScore?: boolean;
      scoreSource?: string;
    }
  ) => trackEvent('radio_play_production_configured', { screenplayId, ...options }),

  renderStarted: (screenplayId: number, provider?: string | null, sceneCount?: number) =>
    trackEvent('radio_play_render_started', { screenplayId, provider, sceneCount }),

  renderCompleted: (screenplayId: number, durationMs: number, musicCostUsd?: number | null) =>
    trackEvent('radio_play_render_completed', {
      screenplayId,
      durationMs,
      musicCostUsd: musicCostUsd ?? undefined,
    }),

  renderFailed: (screenplayId: number, error: string) =>
    trackEvent('radio_play_render_failed', {
      screenplayId,
      error: error.substring(0, 200),
    }),

  renderCancelled: (screenplayId: number) =>
    trackEvent('radio_play_render_cancelled', { screenplayId }),

  scorePanelViewed: (screenplayId: number) =>
    trackEvent('radio_play_score_panel_viewed', { screenplayId }),

  edlViewed: (screenplayId: number) =>
    trackEvent('radio_play_edl_viewed', { screenplayId }),
};

/**
 * Track error events
 */
export function trackError(
  errorType: string,
  errorMessage: string,
  context?: Record<string, any>
): void {
  const pathname = browser ? window.location.pathname : '';
  const onContent = isContentSurface(pathname);
  trackEvent('frontend_error', {
    errorType,
    ...(onContent ? {} : { errorMessage: errorMessage.substring(0, 200) }),
    url: pathname,
    ...sanitizeAnalyticsProperties(context, pathname),
  });
}

/**
 * Get PostHog client for advanced usage
 */
export function syncContentSurface(pathname: string): void {
  if (!isPostHogReady()) return;
  try {
    posthogInstance.stopSessionRecording?.();
    posthogInstance.set_config?.({
      disable_session_recording: true,
      autocapture: !isContentSurface(pathname),
    });
  } catch (error) {
    console.error('[PostHog] Failed to sync content-surface telemetry:', error);
  }
}

export function getPostHog(): any {
  return posthogInstance;
}

// Export analytics object for convenient access
export const analytics = {
  init: initPostHog,
  setConsent: setPostHogConsent,
  identify: identifyUser,
  reset: resetUser,
  track: trackEvent,
  trackPageView,
  trackFeature,
  trackError,
  syncContentSurface,
  classifyReferrer,
  screenplay: screenplayEvents,
  theBiz: theBizEvents,
  studio: studioEvents,
  radioPlay: radioPlayEvents,
  isReady: isPostHogReady,
  getClient: getPostHog,
};

export default analytics;

